import { Link, useLocation, useNavigate } from 'react-router';
import { Box, Tooltip, Avatar } from '@mui/material';
import type { ReactNode } from 'react';
import { useLogoutMutation } from '@/app/services/authApi';
import { useAppSelector } from '@/app/store';
import { selectAuth } from '@/app/features/authSlice';
import { osirisTokens } from '@/theme/tokens';
import { Icon } from '@/components/ui';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  match?: (pathname: string) => boolean;
  adminOnly?: boolean;
  className: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard/drones',
    label: 'Drones / Stations',
    icon: <Icon name="drone-1" />,
    match: (p) => p.startsWith('/dashboard/drones'),
    className: 'sidenav-drones',
  },
  {
    to: '/dashboard/live-sessions',
    label: 'Live Sessions',
    icon: <Icon name="live-streaming" />,
    match: (p) => p.startsWith('/dashboard/live-sessions') || p.startsWith('/dashboard/sessions'),
    className: 'sidenav-live',
  },
  {
    to: '/dashboard/missions',
    label: 'Missions',
    icon: <Icon name="route" />,
    match: (p) => p.startsWith('/dashboard/missions'),
    className: 'sidenav-missions',
  },
  {
    to: '/admin/users',
    label: 'Users',
    icon: <Icon name="user-group" />,
    match: (p) => p.startsWith('/admin/users'),
    adminOnly: true,
    className: 'sidenav-users',
  },
  {
    to: '/admin/apps',
    label: 'Apps',
    icon: <Icon name="grid" />,
    match: (p) => p.startsWith('/admin/apps') || p.startsWith('/dashboard/apps'),
    adminOnly: true,
    className: 'sidenav-apps',
  },

  // Hidden from the nav on purpose — the /dashboard/swarm route stays
  // registered and the page is reachable by URL (see routes/dashboard.tsx)
  // {
  //   to: '/dashboard/swarm',
  //   label: 'Swarm Control',
  //   icon: <Icon name="drone-2" />,
  //   match: (p) => p.startsWith('/dashboard/swarm'),
  //   className: 'sidenav-swarm',
  // },

  {
    to: '/dashboard/trigger-templates',
    label: 'Triggers',
    icon: <Icon name="light-line" />,
    match: (p) => p.startsWith('/dashboard/trigger-templates'),
    className: 'sidenav-triggers',
  },
  {
    to: '/dashboard/swarm-behaviors',
    label: 'Swarm Behaviors',
    icon: <Icon name="drone-2" />,
    match: (p) => p.startsWith('/dashboard/swarm-behaviors'),
    className: 'sidenav-swarm-behaviors',
  },
  {
    to: '/dashboard/image-annotations',
    label: 'Image Annotations',
    icon: <Icon name="annotate" />,
    match: (p) => p.startsWith('/dashboard/image-annotations'),
    className: 'sidenav-annotations',
  },
  {
    to: '/dashboard/training-jobs',
    label: 'Training Jobs',
    icon: <Icon name="cpu" />,
    match: (p) => p.startsWith('/dashboard/training-jobs'),
    className: 'sidenav-training',
  },
  {
    to: '/dashboard/controller-check',
    label: 'Controller Check',
    icon: <Icon name="gamepad-line" />,
    match: (p) => p.startsWith('/dashboard/controller-check'),
    className: 'sidenav-controller',
  },
];

export function SideNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, impersonation } = useAppSelector(selectAuth);
  const [logoutMutation] = useLogoutMutation();
  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    try {
      await logoutMutation().unwrap();
      if (impersonation) {
        navigate('/admin/users');
      } else {
        // Full page load so the branding splash is shown before the auth screen.
        window.location.assign('/auth');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <Box
      className="sidenav"
      sx={{
        gridArea: 'sidenav',
        width: osirisTokens.layout.sidebarWidth,
        bgcolor: osirisTokens.bg.page,
        borderRight: `1px solid ${osirisTokens.border.primary}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pb: 2,
        zIndex: 2,
      }}
    >
      {/* Logo header */}
      <Box
        component={Link}
        to="/"
        className="sidenav-logo"
        sx={{
          height: osirisTokens.layout.headerHeight,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src="/logo.svg"
          alt="OSIRIS AI"
          sx={{ width: 40, height: 18, objectFit: 'contain' }}
        />
      </Box>

      {/* Divider */}
      <Box
        className="sidenav-divider"
        sx={{
          width: 33.5,
          height: '1px',
          bgcolor: osirisTokens.border.tertiary,
          flexShrink: 0,
        }}
      />

      {/* Nav items */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          pt: 4,
          gap: 1,
        }}
      >
        {items.map((item) => {
          const isActive = item.match
            ? item.match(location.pathname)
            : location.pathname === item.to;
          return (
            <Tooltip key={item.to} title={item.label} placement="right">
              <Box
                component={Link}
                to={item.to}
                className={`sidenav-item ${item.className}${isActive ? ' is-active' : ''}`}
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Figma sidenav (28784:69392): selected tile is 2px, rest 4px
                  borderRadius: isActive ? '2px' : '4px',
                  color: isActive ? '#ffffff' : osirisTokens.icons.primary,
                  bgcolor: isActive ? osirisTokens.bg.active : 'transparent',
                  textDecoration: 'none',
                  transition: 'background-color 120ms ease, color 120ms ease',
                  '&:hover': {
                    bgcolor: isActive ? osirisTokens.bg.active : osirisTokens.bg.surface,
                    color: '#ffffff',
                  },
                  // no browser focus ring on the tile; keyboard focus reuses hover
                  outline: 'none',
                  '&:focus-visible': {
                    bgcolor: isActive ? osirisTokens.bg.active : osirisTokens.bg.surface,
                    color: '#ffffff',
                  },
                }}
              >
                {item.icon}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* User + Logout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mt: 2,
        }}
      >
        <Tooltip
          title={
            impersonation
              ? `${user?.name ?? 'User'} — impersonation active`
              : (user?.name ?? 'Profile')
          }
          placement="right"
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              className={`sidenav-avatar${impersonation ? ' is-impersonating' : ''}`}
              sx={{
                width: 40,
                height: 40,
                bgcolor: impersonation ? 'rgba(236, 173, 0, 0.18)' : osirisTokens.border.primary,
                color: impersonation
                  ? osirisTokens.state.impersonation
                  : osirisTokens.icons.primary,
                border: impersonation ? `1px solid ${osirisTokens.state.impersonation}` : 'none',
              }}
            >
              <Icon name="user" />
            </Avatar>
            {impersonation && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: osirisTokens.state.impersonation,
                  border: `2px solid ${osirisTokens.bg.page}`,
                }}
              />
            )}
          </Box>
        </Tooltip>
        <Tooltip title="Logout" placement="right">
          <Box
            component="button"
            onClick={handleLogout}
            className="sidenav-logout"
            sx={{
              all: 'unset',
              cursor: 'pointer',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              color: osirisTokens.icons.primary,
              '&:hover': {
                bgcolor: osirisTokens.bg.surface,
                color: '#ffffff',
              },
            }}
          >
            <Icon name="logout" />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
